import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';

import {
  formatAccountLabel,
  rescanAccount,
  type Account,
} from '@/features/accounts/accounts.api';
import { accountsQueryKey } from '@/features/accounts/useAccounts';

/**
 * Extract the human-readable failure message for a re-scan error.
 *
 * The Hub answers with a specific `detail` string (409 "account is not
 * banned...", 503 "gateway is unavailable for re-scan", 404 "account not
 * found") — surface it verbatim; anything else (network blip) falls back to a
 * generic Chinese message.
 */
function rescanErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string' && detail.length > 0) {
      return detail;
    }
  }
  return '重新扫码失败，请重试';
}

/**
 * Mutation over `POST /api/v1/accounts/{id}/rescan` — repair a banned account.
 *
 * Mirrors `useSetAgentDisabled`: deliberately NOT optimistic (a re-scan wipes
 * the old WhatsApp credentials, so the row should only flip once the server
 * confirms). On success the returned row — now `qr_required` — is written into
 * the `['accounts']` cache in place, so the prop-driven `AccountRow` re-renders
 * out of its banned state and into the existing QR-pairing affordance without a
 * flicker; a success toast confirms. On error the backend `detail` surfaces via
 * a sonner toast.
 *
 * Unlike `useSetAgentDisabled` this does NOT invalidate afterwards: the
 * accounts list is already kept live by `useAccounts`' 30s poll AND the WS
 * `account.status` projection, so an immediate refetch would only race the
 * just-set `qr_required` against a poll — the natural qr_required -> connecting
 * -> connected progression lands via those channels on its own.
 */
export function useRescanAccount(): UseMutationResult<Account, unknown, string> {
  const queryClient = useQueryClient();

  return useMutation<Account, unknown, string>({
    mutationFn: (accountId) => rescanAccount(accountId),

    onSuccess: (updated) => {
      queryClient.setQueryData<Account[]>(accountsQueryKey, (rows) =>
        rows?.map((row) => (row.id === updated.id ? updated : row)),
      );
      toast.success(`已重置 ${formatAccountLabel(updated)}，请重新扫码配对`);
    },

    onError: (error) => {
      toast.error(rescanErrorMessage(error));
    },
  });
}
